import {
  Design,
  Areas,
  Obstacle,
  LogicalSpace,
  Group,
  JustInTimeRoutingConfig,
  InterAreaConnection,
  VehicleType,
  LoadType,
  Flow,
  Floorplan,
  Track,
  TrafficRulesCatalog,
  MetaData,
  Templates,
  FlowStop,
  FlowLayer,
  FloorplanRevision,
  ReferenceImage,
  UxOnly,
  FileReference,
  HighwayArea,
} from '@thive/fddm-types';

import { ICommand } from '@/modules/canvas/commands/ICommand';
import {
  FddmItemUpdates,
  IFddmService,
  TemplateRegeneration,
} from '@/modules/canvas/services/Floorplan/IFddmService';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { IHistoryService } from '@/modules/canvas/services/history/IHistoryService';
import {
  areasToFlatCollection,
  isHighwayArea,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers';
import { Area, Template } from '@/modules/floorplanDesign/types';
import {
  createEmptyAreas,
  createEmptyTemplates,
  getSupportedVehicleIds,
  setSupportedVehicles,
  templatesToFlatCollection,
  pushTemplateToItsCollection,
  hasNoAreas,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmPreferenceValues } from '@/modules/floorplanDesign/preferences';
import { usePointCloudUIStore } from '@/modules/pointCloud/store';
import { TpsServiceFactory } from '@/modules/templating/service/TpsServiceFactory';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';

type TemplateRestoreData = {
  template: Template;
  areas: Areas;
  interAreaConnections: InterAreaConnection[];
  flowLayer: FlowLayer;
};

export class FddmServiceHistoryDecorator implements IFddmService {
  private decoree: IFddmService;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private pointCloudUIStore: typeof usePointCloudUIStore;
  private historySvc: IHistoryService;

  constructor(
    decoree: IFddmService,
    floorplanDesignStore: typeof useFloorplanDesignStore,
    pointCloudUIStore: typeof usePointCloudUIStore,
  ) {
    this.decoree = decoree;
    this.floorplanDesignStore = floorplanDesignStore;
    this.pointCloudUIStore = pointCloudUIStore;
    this.historySvc = HistoryServiceFactory.getInstance().createHistoryService();
  }

  initialize(revision: FloorplanRevision): void {
    return this.decoree.initialize(revision);
  }

  async regenerateAll(): Promise<void> {
    await this.decoree.regenerateAll();
  }

  getModel(): FloorplanRevision {
    return this.decoree.getModel();
  }

  //
  // MetaData
  //

  getMetaData(): MetaData {
    return this.decoree.getMetaData();
  }

  setMetaData(metaData: MetaData): void {
    this.decoree.setMetaData(metaData);
  }

  //
  // Reference image
  //

  getReferenceImage(): UxOnly['referenceImage'] {
    return this.decoree.getReferenceImage();
  }

  addReferenceImage(referenceImage: ReferenceImage): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addReferenceImage(referenceImage);
      },
      undo: () => {
        this.decoree.deleteReferenceImage();
      },
    });
  }

  updateReferenceImage(referenceImage: ReferenceImage): void {
    const currentReferenceImage = this.decoree.getReferenceImage();

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateReferenceImage(referenceImage);
      },
      undo: () => {
        this.decoree.updateReferenceImage(currentReferenceImage);
      },
    });
  }

  deleteReferenceImage(): void {
    const currentReferenceImage = this.decoree.getReferenceImage();

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteReferenceImage();
      },
      undo: () => {
        if (currentReferenceImage) {
          this.decoree.addReferenceImage(currentReferenceImage);
        }
      },
    });
  }

  //
  // Point cloud
  //

  addPointCloudInputFileRef(fileRef: string): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addPointCloudInputFileRef(fileRef);
      },
      undo: () => {
        this.decoree.deletePointCloudInputFileRef();
      },
    });
  }

  addPointCloudOutputFileRef(fileRef: string): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addPointCloudOutputFileRef(fileRef);
      },
      undo: () => {
        this.decoree.deletePointCloudOutputFileRef();
      },
    });
  }

  deletePointCloudInputFileRef(): void {
    const current = this.floorplanDesignStore.getState().uxonly.pointCloudInputFileRef;

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deletePointCloudInputFileRef();
      },
      undo: () => {
        if (current) {
          this.decoree.addPointCloudInputFileRef(current);
        }
      },
    });
  }

  deletePointCloudOutputFileRef(): void {
    const current = this.floorplanDesignStore.getState().uxonly.pointCloudOutputFileRef;

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deletePointCloudOutputFileRef();
      },
      undo: () => {
        if (current) {
          this.decoree.addPointCloudOutputFileRef(current);
        }
      },
    });
  }

  //
  // Design
  //

  getDesign(): Design {
    return this.decoree.getDesign();
  }

  setDesign(design: Design): void {
    const current: Design = this.decoree.getDesign();

    this.excuteAndTrack({
      execute: () => {
        this.decoree.setDesign(design);
      },
      undo: () => {
        this.decoree.setDesign(current);
      },
    });
  }

  //
  // Areas
  //

  getArea(id: string): Area | undefined {
    return this.decoree.getArea(id);
  }

  async addAreas(areas: Areas): Promise<void> {
    this.excuteAndTrack({
      execute: async () => {
        await this.decoree.addAreas(areas);
      },
      undo: () => {
        this.decoree.deleteAreas(areas);
      },
    });
  }

  updateArea(areaUpdate: Area): void {
    const previousArea = this.getAreaSnapshot(areaUpdate.id);
    let committedAreaUpdate: Area | undefined;

    this.excuteAndTrack({
      execute: () => {
        const areaToApply = committedAreaUpdate ?? areaUpdate;

        this.decoree.updateArea(structuredClone(areaToApply));

        if (!committedAreaUpdate) {
          committedAreaUpdate = this.getAreaSnapshot(areaUpdate.id);
        }
      },
      undo: () => {
        this.decoree.updateArea(structuredClone(previousArea));
      },
    });
  }

  updateAreas(areaUpdates: Areas): void {
    const previousAreas = this.getAreaSnapshots(areaUpdates);
    let committedAreaUpdates: Areas | undefined;

    this.excuteAndTrack({
      execute: () => {
        const areaUpdatesToApply = committedAreaUpdates ?? areaUpdates;

        this.decoree.updateAreas(structuredClone(areaUpdatesToApply));

        if (!committedAreaUpdates) {
          committedAreaUpdates = this.getAreaSnapshots(areaUpdates);
        }
      },
      undo: () => {
        this.decoree.updateAreas(structuredClone(previousAreas));
      },
    });
  }

  deleteAreas(areas: Areas): void {
    const deletedHighwayIds = new Set(
      areasToFlatCollection(areas)
        .filter(isHighwayArea)
        .map((a) => a.id),
    );
    const { design, getHighwayBySegmentId } = useFloorplanDesignStore.getState();
    const jitConfigsToRestore =
      deletedHighwayIds.size > 0
        ? design.justInTimeRoutingConfigs.filter((config) =>
            deletedHighwayIds.has(getHighwayBySegmentId(config.highwayAreaId)?.id ?? ''),
          )
        : [];

    // deleteAreas also removes these, so undo has to put them back
    const deletedAreaIds = areasToFlatCollection(areas).map(({ id }) => id);
    const deletedSegmentIds = areasToFlatCollection(areas)
      .filter(isHighwayArea)
      .flatMap((area) => area.segments.map((segment) => segment.id));
    const flowStopsToRestore = structuredClone(
      this.floorplanDesignStore.getState().getRelatedFlowStops(deletedAreaIds),
    );
    const flowsToRestore = structuredClone(
      this.floorplanDesignStore
        .getState()
        .getRelatedFlows([...deletedAreaIds, ...flowStopsToRestore.map(({ id }) => id)]),
    );
    const connectionsToRestore = structuredClone(
      this.floorplanDesignStore
        .getState()
        .interAreaConnectionActions.getRelatedTo([...deletedAreaIds, ...deletedSegmentIds]),
    );

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteAreas(areas);
      },
      undo: () => {
        this.decoree.addAreas(areas);
        this.restoreFlowStops(flowStopsToRestore);

        if (connectionsToRestore.length > 0) {
          this.decoree.addInterAreaConnections(connectionsToRestore);
        }

        // Flows of flow stops that were only trimmed weren't deleted
        const deletedFlows = flowsToRestore.filter(({ id }) => !this.decoree.getFlow(id));
        if (deletedFlows.length > 0) {
          this.decoree.addFlows(deletedFlows);
        }

        if (jitConfigsToRestore.length > 0) {
          this.decoree.addJitRoutingConfigs(jitConfigsToRestore);
        }
      },
    });
  }

  //
  // Obstacles
  //

  getObstacle(id: string): Obstacle | undefined {
    return this.decoree.getObstacle(id);
  }

  async addObstacles(obstacles: Obstacle[]): Promise<void> {
    this.excuteAndTrack({
      execute: async () => {
        await this.decoree.addObstacles(obstacles);
      },
      undo: () => {
        this.decoree.deleteObstacles(obstacles);
      },
    });
  }

  updateObstacle(obstacle: Obstacle): void {
    const current: Obstacle | undefined = this.decoree.getObstacle(obstacle.id);

    if (!current) {
      throw new Error('Obstacle cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateObstacle(obstacle);
      },
      undo: () => {
        this.decoree.updateObstacle(current);
      },
    });
  }

  updateObstacles(obstacles: Obstacle[]): void {
    const current: Obstacle[] = obstacles.map((obstacle) => {
      const currentObstacle = this.decoree.getObstacle(obstacle.id);

      if (!currentObstacle) {
        throw new Error('Obstacle cannot be undefined.');
      }

      return currentObstacle;
    });

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateObstacles(obstacles);
      },
      undo: () => {
        this.decoree.updateObstacles(current);
      },
    });
  }

  updateItems({
    areas = createEmptyAreas(),
    obstacles = [],
    logicalSpaces = [],
  }: FddmItemUpdates): void {
    const previousAreas = this.getAreaSnapshots(areas);
    const previousObstacles = obstacles.map(({ id }) => {
      const currentObstacle = this.decoree.getObstacle(id);

      if (!currentObstacle) {
        throw new Error('Obstacle cannot be undefined.');
      }

      return currentObstacle;
    });
    const previousLogicalSpaces = logicalSpaces.map(({ id }) => {
      const currentLogicalSpace = this.decoree.getLogicalSpace(id);

      if (!currentLogicalSpace) {
        throw new Error('LogicalSpace cannot be undefined.');
      }

      return currentLogicalSpace;
    });
    // Same as updateAreas: redo applies the areas as they ended up after the first update
    let committedAreaUpdates: Areas | undefined;

    this.excuteAndTrack({
      execute: () => {
        const areaUpdatesToApply = committedAreaUpdates ?? areas;

        this.decoree.updateItems({
          areas: structuredClone(areaUpdatesToApply),
          obstacles,
          logicalSpaces,
        });

        if (!committedAreaUpdates) {
          committedAreaUpdates = this.getAreaSnapshots(areas);
        }
      },
      undo: () => {
        this.decoree.updateItems({
          areas: structuredClone(previousAreas),
          obstacles: previousObstacles,
          logicalSpaces: previousLogicalSpaces,
        });
      },
    });
  }

  deleteObstacles(obstacles: Obstacle[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteObstacles(obstacles);
      },
      undo: () => {
        this.decoree.addObstacles(obstacles);
      },
    });
  }

  //
  // Inter Area Connections
  //

  getInterAreaConnection(id: string): InterAreaConnection | undefined {
    return this.decoree.getInterAreaConnection(id);
  }

  addInterAreaConnections(connections: InterAreaConnection[]): boolean {
    let result = false;

    this.excuteAndTrack({
      execute: () => {
        result = this.decoree.addInterAreaConnections(connections);
      },
      undo: () => {
        this.decoree.deleteInterAreaConnections(connections);
      },
    });

    return result;
  }

  updateInterAreaConnections(connections: InterAreaConnection[]): void {
    const current: InterAreaConnection[] = connections.map((connection) => {
      const currentConnection = this.decoree.getInterAreaConnection(connection.id);

      if (!currentConnection) {
        throw new Error('InterAreaConnection cannot be undefined.');
      }

      return currentConnection;
    });

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateInterAreaConnections(connections);
      },
      undo: () => {
        this.decoree.updateInterAreaConnections(current);
      },
    });
  }

  deleteInterAreaConnections(connections: InterAreaConnection[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteInterAreaConnections(connections);
      },
      undo: () => {
        this.decoree.addInterAreaConnections(connections);
      },
    });
  }

  //
  // Logical Spaces
  //

  getLogicalSpace(id: string): LogicalSpace | undefined {
    return this.decoree.getLogicalSpace(id);
  }

  async addLogicalSpaces(logicalSpaces: LogicalSpace[]): Promise<void> {
    this.excuteAndTrack({
      execute: async () => {
        await this.decoree.addLogicalSpaces(logicalSpaces);
      },
      undo: () => {
        this.decoree.deleteLogicalSpaces(logicalSpaces);
      },
    });
  }

  updateLogicalSpace(logicalSpace: LogicalSpace): void {
    const current: LogicalSpace | undefined = this.decoree.getLogicalSpace(logicalSpace.id);

    if (!current) {
      throw new Error('LogicalSpace cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateLogicalSpace(logicalSpace);
      },
      undo: () => {
        this.decoree.updateLogicalSpace(current);
      },
    });
  }

  deleteLogicalSpaces(logicalSpaces: LogicalSpace[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteLogicalSpaces(logicalSpaces);
      },
      undo: () => {
        this.decoree.addLogicalSpaces(logicalSpaces);
      },
    });
  }

  //
  // Templates
  //

  getTemplate(id: string): Template | undefined {
    return this.decoree.getTemplate(id);
  }

  addTemplates(templates: Templates): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addTemplates(templates);
      },
      undo: () => {
        this.decoree.deleteTemplates(templates);
      },
    });
  }

  async addTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
  ): Promise<void> {
    const templates = createEmptyTemplates();
    pushTemplateToItsCollection(templates, template);

    await this.excuteAndTrackAsync({
      execute: () => this.decoree.addTemplate(template, areas, interAreaConnections, flowLayer),
      undo: () => {
        this.decoree.deleteTemplates(templates);
      },
    });
  }

  updateTemplate(template: Template): void {
    const currentTemplate = this.decoree.getTemplate(template.group.id);

    if (!currentTemplate) {
      throw new Error('Template cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateTemplate(template);
      },
      undo: () => {
        this.decoree.updateTemplate(currentTemplate);
      },
    });
  }

  async regenerateTemplates(regenerations: TemplateRegeneration[]): Promise<void> {
    const { getTemplateByIdSilently, getTemplateByAreaId } = this.floorplanDesignStore.getState();

    const regenerationsWithCurrent = regenerations.map((regeneration) => {
      const { template, previousAreas } = regeneration;
      const previousAreaIds = areasToFlatCollection(previousAreas).map((area) => area.id);

      const currentTemplate =
        getTemplateByIdSilently(template.group.id) ??
        previousAreaIds.map((areaId) => getTemplateByAreaId(areaId)).find(Boolean);

      return { regeneration, currentTemplate };
    });

    // A template that isn't in FDDM (anymore) is added instead, one after the other
    await regenerationsWithCurrent.reduce(
      (previousAdd, { regeneration, currentTemplate }) =>
        previousAdd.then(async () => {
          if (currentTemplate) return;
          const { template, areas, interAreaConnections, flowLayer } = regeneration;
          await this.addTemplate(template, areas, interAreaConnections, flowLayer);
        }),
      Promise.resolve(),
    );

    const templatesToRegenerate = regenerationsWithCurrent.flatMap(
      ({ regeneration, currentTemplate }) =>
        currentTemplate ? [{ regeneration, previousTemplate: currentTemplate }] : [],
    );
    if (templatesToRegenerate.length === 0) return;

    /*
     * Undo and redo generate the templates again with TPS, with their params from before or after,
     * instead of restoring the stored designs. These are the ids the templates have in FDDM now
     * (regenerating can change them), in the order of templatesToRegenerate.
     */
    let appliedTemplateIds = templatesToRegenerate.map(
      ({ regeneration }) => regeneration.template.group.id,
    );
    let isFirstExecution = true;

    const regenerateWithTps = async (templates: Template[]): Promise<void> => {
      appliedTemplateIds = await this.regenerateTemplatesWithTps(templates, appliedTemplateIds);
    };

    // One undo step for all regenerated templates
    await this.excuteAndTrackAsync({
      execute: async () => {
        if (!isFirstExecution) {
          await regenerateWithTps(
            templatesToRegenerate.map(({ regeneration }) => regeneration.template),
          );
          return;
        }

        // The first time, the designs were just generated: apply them
        isFirstExecution = false;
        await this.decoree.regenerateTemplates(
          templatesToRegenerate.map(({ regeneration }) => regeneration),
        );
      },
      undo: () =>
        regenerateWithTps(templatesToRegenerate.map(({ previousTemplate }) => previousTemplate)),
    });
  }

  /**
   * @description Generates the templates with TPS and puts them in place of the templates with
   * `currentTemplateIds` (same order). Returns the ids the templates have afterwards; a template
   * that failed to generate keeps its current id.
   */
  private async regenerateTemplatesWithTps(
    templates: Template[],
    currentTemplateIds: string[],
  ): Promise<string[]> {
    const loadingSvc = LoadingServiceFactory.getInstance().createLoadingService();
    // Same loader as regenerating from the properties: TPS and the floorplan take a while
    loadingSvc.showLoadContainer('TRANSPARENT');

    try {
      const designs = await TpsServiceFactory.getInstance()
        .createTpsService()
        .fetchTemplateDesigns(templates);

      // Read after generating: the store may have changed meanwhile
      const { getTemplateByIdSilently, getAreasByIds } = this.floorplanDesignStore.getState();
      const regenerations = currentTemplateIds.map((currentTemplateId, index) => {
        const design = designs[index];
        const template = design && templatesToFlatCollection(design.templates).at(0);
        const currentTemplate = getTemplateByIdSilently(currentTemplateId);
        if (!design || !template || !currentTemplate) return undefined;

        return {
          template,
          areas: design.areas,
          interAreaConnections: design.interAreaConnections,
          flowLayer: design.flows,
          previousAreas: getAreasByIds(currentTemplate.group.areaIds),
        };
      });

      await this.decoree.regenerateTemplates(
        regenerations.flatMap((regeneration) => (regeneration ? [regeneration] : [])),
      );

      return currentTemplateIds.map(
        (currentTemplateId, index) => regenerations[index]?.template.group.id ?? currentTemplateId,
      );
    } finally {
      loadingSvc.hideLoadContainer();
    }
  }

  updateTemplates(templates: Templates): void {
    const current: Templates = createEmptyTemplates();

    templatesToFlatCollection(templates).forEach((template) => {
      const currentTemplate = this.decoree.getTemplate(template.group.id);

      if (!currentTemplate) {
        throw new Error('Template cannot be undefined.');
      }

      pushTemplateToItsCollection(current, currentTemplate);
    });

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateTemplates(templates);
      },
      undo: () => {
        this.decoree.updateTemplates(current);
      },
    });
  }

  deleteTemplates(templates: Templates): void {
    const templatesToRestore = templatesToFlatCollection(templates).map((template) =>
      this.getTemplateRestoreData(template),
    );

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteTemplates(templates);
      },
      undo: () => {
        templatesToRestore.forEach((templateToRestore) => {
          this.decoree.addTemplate(
            templateToRestore.template,
            templateToRestore.areas,
            templateToRestore.interAreaConnections,
            templateToRestore.flowLayer,
          );
        });
      },
    });
  }
  //
  // Groups
  //

  getGroup(id: string): Group | undefined {
    return this.decoree.getGroup(id);
  }

  addGroups(groups: Group[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addGroups(groups);
      },
      undo: () => {
        this.decoree.ungroupGroups(groups);
      },
    });
  }

  updateGroup(group: Group): void {
    const current: Group | undefined = this.decoree.getGroup(group.id);

    if (!current) {
      throw new Error('Group cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateGroup(group);
      },
      undo: () => {
        this.decoree.updateGroup(current);
      },
    });
  }

  deleteGroups(groups: Group[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteGroups(groups);
      },
      undo: () => {
        this.decoree.addGroups(groups);
      },
    });
  }

  ungroupGroups(groups: Group[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.ungroupGroups(groups);
      },
      undo: () => {
        this.decoree.addGroups(groups);
      },
    });
  }

  //
  // Jit Routing
  //

  getJitRoutingConfig(id: string): JustInTimeRoutingConfig | undefined {
    return this.decoree.getJitRoutingConfig(id);
  }

  addJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addJitRoutingConfigs(jitRoutingConfigs);
      },
      undo: () => {
        this.decoree.deleteJitRoutingConfigs(jitRoutingConfigs);
      },
    });
  }

  updateJitRoutingConfig(jitRoutingConfig: JustInTimeRoutingConfig): void {
    const current: JustInTimeRoutingConfig | undefined = this.decoree.getJitRoutingConfig(
      jitRoutingConfig.id,
    );

    if (!current) {
      throw new Error('jitRoutingConfig cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateJitRoutingConfig(jitRoutingConfig);
      },
      undo: () => {
        this.decoree.updateJitRoutingConfig(current);
      },
    });
  }

  deleteJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteJitRoutingConfigs(jitRoutingConfigs);
      },
      undo: () => {
        this.decoree.addJitRoutingConfigs(jitRoutingConfigs);
      },
    });
  }

  //
  // Flows
  //

  getFlow(id: string): Flow | undefined {
    return this.decoree.getFlow(id);
  }

  getFlowsByAreaId(areaId: string): Flow[] | undefined {
    return this.decoree.getFlowsByAreaId(areaId);
  }

  addFlows(flows: Flow[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addFlows(flows);
      },
      undo: () => {
        this.decoree.deleteFlows(flows);
      },
    });
  }

  updateFlow(flow: Flow): void {
    const current: Flow | undefined = this.decoree.getFlow(flow.id);

    if (!current) {
      throw new Error('Flow cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateFlow(flow);
      },
      undo: () => {
        this.decoree.updateFlow(current);
      },
    });
  }

  updateFlows(flows: Flow[]): void {
    const current: Flow[] = flows.map((flow) => {
      const existing = this.decoree.getFlow(flow.id);

      if (!existing) {
        throw new Error('Flow cannot be undefined.');
      }

      return existing;
    });

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateFlows(flows);
      },
      undo: () => {
        this.decoree.updateFlows(current);
      },
    });
  }

  deleteFlows(flows: Flow[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteFlows(flows);
      },
      undo: () => {
        this.decoree.addFlows(flows);
      },
    });
  }

  //
  // FlowStops
  //

  getFlowStop(id: string): FlowStop | undefined {
    return this.decoree.getFlowStop(id);
  }

  getFlowStopByAreaId(areaId: string): FlowStop | undefined {
    return this.decoree.getFlowStopByAreaId(areaId);
  }

  addFlowStops(flowStops: FlowStop[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addFlowStops(flowStops);
      },
      undo: () => {
        this.decoree.deleteFlowStops(flowStops);
      },
    });
  }

  updateFlowStop(flowStop: FlowStop): void {
    const current: FlowStop | undefined = this.decoree.getFlowStop(flowStop.id);

    if (!current) {
      throw new Error('FlowStop cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateFlowStop(flowStop);
      },
      undo: () => {
        this.decoree.updateFlowStop(current);
      },
    });
  }

  deleteFlowStops(flowStops: FlowStop[]): void {
    const flowStopIds = flowStops.map((flowStop) => flowStop.id);
    const relatedFlows = this.floorplanDesignStore.getState().getRelatedFlows(flowStopIds);

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteFlowStops(flowStops);
      },
      undo: () => {
        this.decoree.addFlowStops(flowStops);
        this.decoree.addFlows(relatedFlows);
      },
    });
  }

  //
  // Vehicles
  //

  getVehicleType(id: string): VehicleType | undefined {
    return this.decoree.getVehicleType(id);
  }

  addVehicleTypes(vehicleTypes: VehicleType[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addVehicleTypes(vehicleTypes);
      },
      undo: () => {
        this.decoree.deleteVehicleTypes(vehicleTypes);
      },
    });
  }

  addVehiclesToHighways(vehicleTypes: FloorplanRevision['setup']['vehicleTypes']): void {
    if (vehicleTypes.length === 0) {
      return;
    }

    const {
      design: { areas },
    } = this.floorplanDesignStore.getState();

    const currentHighways: HighwayArea[] = [];
    const updatedHighways: HighwayArea[] = [];

    areas.highwayAreas.forEach((area) => {
      const currentSupportedVehicleIds = getSupportedVehicleIds(area);
      const newSupportedVehicleIds = [...currentSupportedVehicleIds];

      vehicleTypes.forEach((vehicleType) => {
        if (!newSupportedVehicleIds.includes(vehicleType.id)) {
          newSupportedVehicleIds.push(vehicleType.id);
        }
      });

      if (currentSupportedVehicleIds.length === newSupportedVehicleIds.length) {
        return;
      }

      currentHighways.push(area);
      updatedHighways.push(setSupportedVehicles(area, newSupportedVehicleIds));
    });

    if (updatedHighways.length === 0) {
      return;
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.addVehiclesToHighways(vehicleTypes);
      },
      undo: () => {
        this.decoree.updateAreas({
          highwayAreas: currentHighways,
          rackingAreas: [],
          singularStationAreas: [],
          stationaryAreas: [],
          manualAreas: [],
        });
      },
    });
  }

  updateVehicleType(vehicleType: VehicleType): void {
    const current: VehicleType | undefined = this.decoree.getVehicleType(vehicleType.id);

    if (!current) {
      throw new Error('VehicleType cannot be undefined.');
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.updateVehicleType(vehicleType);
      },
      undo: () => {
        this.decoree.updateVehicleType(current);
      },
    });
  }

  deleteVehicleTypes(vehicleTypes: VehicleType[]): void {
    const deletedVehicleTypeIds = new Set(vehicleTypes.map((vehicleType) => vehicleType.id));

    const areasToRestore = createEmptyAreas();
    areasToFlatCollection(this.floorplanDesignStore.getState().getModel().design.areas).forEach(
      (area) => {
        const hasDeletedVehicle = getSupportedVehicleIds(area).some((vehicleTypeId) =>
          deletedVehicleTypeIds.has(vehicleTypeId),
        );

        if (!hasDeletedVehicle) {
          return;
        }

        pushAreaToItsCollection(areasToRestore, area);
      },
    );

    const logicalSpacesToRestore = this.floorplanDesignStore
      .getState()
      .getModel()
      .design.logicalSpaces.filter((logicalSpace) =>
        logicalSpace.trafficRules.some(
          (rule) =>
            rule.type === 'VEHICLE_MODE' &&
            !!rule.vehicleModeExtension?.vehicleTypeId &&
            deletedVehicleTypeIds.has(rule.vehicleModeExtension.vehicleTypeId),
        ),
      );

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteVehicleTypes(vehicleTypes);
      },
      undo: () => {
        this.decoree.addVehicleTypes(vehicleTypes);

        if (!hasNoAreas(areasToRestore)) {
          this.decoree.updateAreas(areasToRestore);
        }

        logicalSpacesToRestore.forEach((logicalSpace) => {
          this.decoree.updateLogicalSpace(logicalSpace);
        });
      },
    });
  }

  //
  // Load Carriers
  //

  getLoadType(id: string): LoadType | undefined {
    return this.decoree.getLoadType(id);
  }

  addLoadTypes(loadTypes: LoadType[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addLoadTypes(loadTypes);
      },
      undo: () => {
        this.decoree.deleteLoadTypes(loadTypes);
      },
    });
  }

  deleteLoadTypes(loadTypes: LoadType[]): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteLoadTypes(loadTypes);
      },
      undo: () => {
        this.decoree.addLoadTypes(loadTypes);
      },
    });
  }

  //
  // Catalog
  //

  setCatalogTrafficRules(trafficRules: TrafficRulesCatalog): void {
    const current = this.floorplanDesignStore.getState().uxonly.catalog.trafficRules;

    this.excuteAndTrack({
      execute: () => {
        this.decoree.setCatalogTrafficRules(trafficRules);
      },
      undo: () => {
        this.decoree.setCatalogTrafficRules(current);
      },
    });
  }

  getAllFileReferences(): FileReference[] {
    return this.decoree.getAllFileReferences();
  }

  getFileReferences(type: FileReference['type']): FileReference[] {
    return this.decoree.getFileReferences(type);
  }

  addFileReference(fileReference: FileReference): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.addFileReference(fileReference);
      },
      undo: () => {
        this.decoree.deleteFileReference(fileReference);
      },
    });
  }

  deleteFileReference(fileReference: FileReference): void {
    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteFileReference(fileReference);
      },
      undo: () => {
        this.decoree.addFileReference(fileReference);
      },
    });
  }

  //
  // Floorplan
  //

  getFloorplan(): Floorplan | undefined {
    return this.decoree.getFloorplan();
  }

  setFloorplan(floorplan: Floorplan): void {
    this.decoree.setFloorplan(floorplan);
  }

  //
  // Tracks
  //

  getTracks(): Track[] | undefined {
    return this.decoree.getTracks();
  }

  setTracks(tracks: Track[]): void {
    this.decoree.setTracks(tracks);
  }

  //
  // Selection
  //

  deleteSelection(selectedIds: string[]): void {
    if (selectedIds.length === 0) return;

    const selectedGroups = this.floorplanDesignStore.getState().getGroupsByIds(selectedIds);
    const selectedGroupChildIds = selectedGroups.flatMap((group) => group.areaIds);

    const idsToDelete = [...new Set([...selectedIds, ...selectedGroupChildIds])];

    const templates = this.floorplanDesignStore.getState().getTemplatesByIds(selectedIds);
    const templatesToRestore = templatesToFlatCollection(templates).map((template) =>
      this.getTemplateRestoreData(template),
    );

    const templateAreaIds = new Set(
      templatesToFlatCollection(templates).flatMap((template) => template.group.areaIds),
    );

    const selectedAreas = this.floorplanDesignStore.getState().getAreasByIds(idsToDelete);
    const areas = createEmptyAreas();

    areasToFlatCollection(selectedAreas).forEach((area) => {
      if (!templateAreaIds.has(area.id)) {
        pushAreaToItsCollection(areas, area);
      }
    });

    const obstacles = this.floorplanDesignStore.getState().getObstaclesByIds(idsToDelete);
    const logicalSpaces = this.floorplanDesignStore.getState().getLogicalSpacesByIds(idsToDelete);
    const selectedFlows = idsToDelete.flatMap((id) => {
      const flow = this.decoree.getFlow(id);

      return flow ? [flow] : [];
    });

    const affectedGroups = this.floorplanDesignStore.getState().getGroupsByAreaIds(idsToDelete);
    const groupsToRestore = [
      ...selectedGroups,
      ...affectedGroups.filter(
        (affectedGroup) => !selectedGroups.some((group) => group.id === affectedGroup.id),
      ),
    ];

    const affectedAreaIds = areasToFlatCollection(areas).flatMap((area) =>
      isHighwayArea(area) ? area.segments.map((segment) => segment.id) : area.id,
    );

    const relatedFlows = this.floorplanDesignStore.getState().getRelatedFlows(affectedAreaIds);
    const flowsToRestoreById = new Map<string, Flow>();
    [...relatedFlows, ...selectedFlows].forEach((flow) => {
      flowsToRestoreById.set(flow.id, flow);
    });
    const flowsToRestore = [...flowsToRestoreById.values()];

    // deleteAreas deletes or trims these, so undo has to put them back (before the flows)
    const flowStopsToRestore = structuredClone(
      this.floorplanDesignStore
        .getState()
        .getRelatedFlowStops(areasToFlatCollection(areas).map(({ id }) => id)),
    );

    const relatedConnections = this.floorplanDesignStore
      .getState()
      .interAreaConnectionActions.getRelatedTo(affectedAreaIds);

    const deletedHighwayIds = new Set(
      areasToFlatCollection(areas)
        .filter(isHighwayArea)
        .map((a) => a.id),
    );
    const { design, getHighwayBySegmentId } = useFloorplanDesignStore.getState();
    const jitConfigsToRestore =
      deletedHighwayIds.size > 0
        ? design.justInTimeRoutingConfigs.filter((config) =>
            deletedHighwayIds.has(getHighwayBySegmentId(config.highwayAreaId)?.id ?? ''),
          )
        : [];

    this.excuteAndTrack({
      execute: () => {
        this.decoree.deleteSelection(selectedIds);
      },
      undo: () => {
        if (!hasNoAreas(areas)) {
          this.decoree.addAreas(areas);
        }

        if (logicalSpaces.length > 0) {
          this.decoree.addLogicalSpaces(logicalSpaces);
        }

        if (obstacles.length > 0) {
          this.decoree.addObstacles(obstacles);
        }

        groupsToRestore.forEach((group) => {
          if (this.decoree.getGroup(group.id)) {
            this.decoree.updateGroup(group);
            return;
          }

          this.decoree.addGroups([group]);
        });

        templatesToRestore.forEach((templateToRestore) => {
          this.decoree.addTemplate(
            templateToRestore.template,
            templateToRestore.areas,
            templateToRestore.interAreaConnections,
            templateToRestore.flowLayer,
          );
        });

        this.restoreFlowStops(flowStopsToRestore);

        if (relatedConnections.length > 0) {
          this.decoree.addInterAreaConnections(relatedConnections);
        }

        if (flowsToRestore.length > 0) {
          this.decoree.addFlows(flowsToRestore);
        }

        if (jitConfigsToRestore.length > 0) {
          this.decoree.addJitRoutingConfigs(jitConfigsToRestore);
        }
      },
    });
  }

  mirrorSelectionHorizontally(selectedIds: string[]): void {
    if (selectedIds.length === 0) {
      return;
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.mirrorSelectionHorizontally(selectedIds);
      },
      undo: () => {
        this.decoree.mirrorSelectionHorizontally(selectedIds);
      },
    });
  }

  mirrorSelectionVertically(selectedIds: string[]): void {
    if (selectedIds.length === 0) {
      return;
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.mirrorSelectionVertically(selectedIds);
      },
      undo: () => {
        this.decoree.mirrorSelectionVertically(selectedIds);
      },
    });
  }

  rotateSelection90Degrees(selectedIds: string[]): void {
    if (selectedIds.length === 0) {
      return;
    }

    this.excuteAndTrack({
      execute: () => {
        this.decoree.rotateSelection90Degrees(selectedIds);
      },
      undo: () => {
        this.decoree.rotateSelection90Degrees(selectedIds);
        this.decoree.rotateSelection90Degrees(selectedIds);
        this.decoree.rotateSelection90Degrees(selectedIds);
      },
    });
  }

  //
  // Preferences
  //

  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    return this.decoree.getPreference(key);
  }

  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void {
    const currentValue = this.decoree.getPreference(key);

    this.excuteAndTrack({
      execute: () => {
        this.decoree.setPreference(key, value);
      },
      undo: () => {
        this.decoree.setPreference(key, currentValue);
      },
    });
  }

  resetPreferences(): void {
    this.decoree.resetPreferences();
  }

  triggerAutosave(): void {
    this.decoree.triggerAutosave();
  }

  changeName(id: string, newName: string): boolean {
    let result = false;
    let current: string;
    const template = this.decoree.getTemplate(id);
    const area = this.decoree.getArea(id);
    const obstacle = this.decoree.getObstacle(id);
    const logicalSpace = this.decoree.getLogicalSpace(id);
    const group = this.decoree.getGroup(id);

    if (template) {
      current = template.group.name;
    } else if (area) {
      current = area.name;
    } else if (obstacle) {
      current = obstacle.name;
    } else if (logicalSpace) {
      current = logicalSpace.name;
    } else if (group) {
      current = group.name;
    } else {
      throw new Error('Entity with the given id cannot be found.');
    }

    this.excuteAndTrack({
      execute: () => {
        result = this.decoree.changeName(id, newName);
      },
      undo: () => {
        this.decoree.changeName(id, current);
      },
    });

    return result;
  }

  disposeState(): void {
    this.decoree.disposeState();
  }

  //
  // Utility methods
  //

  /**
   * Creates a deep clone snapshot of a single area for undo support.
   * We need a deep clone (not a reference) because the area object may be mutated during
   * the execute phase. If we only stored a reference, the "before" snapshot would also
   * be mutated, making undo impossible. structuredClone ensures complete independence.
   */
  private getAreaSnapshot(areaId: string): Area {
    const area = this.decoree.getArea(areaId);

    if (!area) {
      throw new Error(`Area with id ${areaId} cannot be found.`);
    }

    return structuredClone(area);
  }

  /**
   * Creates deep clone snapshots of multiple areas, preserving the Areas collection structure.
   * See getAreaSnapshot for why we clone: to ensure the "before" state is immutable and
   * independent from mutations that occur during the execute phase.
   */
  private getAreaSnapshots(areaReferences: Areas): Areas {
    const areaSnapshots = createEmptyAreas();

    areasToFlatCollection(areaReferences).forEach((areaReference) => {
      pushAreaToItsCollection(areaSnapshots, this.getAreaSnapshot(areaReference.id));
    });

    return areaSnapshots;
  }

  private getTemplateRestoreData(template: Template): TemplateRestoreData {
    const { areaIds } = template.group;

    const areas = this.floorplanDesignStore.getState().getAreasByIds(areaIds);

    const interAreaConnections = this.floorplanDesignStore
      .getState()
      .interAreaConnectionActions.getRelatedTo(areaIds);

    const flowStops = this.floorplanDesignStore
      .getState()
      .design.flows.flowStops.filter((flowStop) =>
        flowStop.areaIds.some((areaId) => areaIds.includes(areaId)),
      );

    const flowStopIds = flowStops.map((flowStop) => flowStop.id);

    const flows = this.floorplanDesignStore
      .getState()
      .getRelatedFlows([...areaIds, ...flowStopIds]);

    return {
      template,
      areas,
      interAreaConnections,
      flowLayer: {
        flowStops,
        flows,
        dependentFlows: [],
      },
    };
  }

  /**
   * @description Puts flow stops back as they were before a delete: deleteAreas deletes the flow
   * stops that have no areas left and removes the deleted areas from the others. Call it after the
   * areas are restored and before the flows, which can point to these flow stops.
   */
  private restoreFlowStops(flowStops: FlowStop[]): void {
    const deletedFlowStops = flowStops.filter(({ id }) => !this.decoree.getFlowStop(id));
    const deletedIds = new Set(deletedFlowStops.map(({ id }) => id));

    if (deletedFlowStops.length > 0) {
      this.decoree.addFlowStops(deletedFlowStops);
    }

    flowStops
      .filter(({ id }) => !deletedIds.has(id))
      .forEach((flowStop) => this.decoree.updateFlowStop(flowStop));
  }

  private excuteAndTrack(command: ICommand): void {
    command.execute();
    this.historySvc.track(command);
  }

  private async excuteAndTrackAsync(command: ICommand): Promise<void> {
    await command.execute();
    this.historySvc.track(command);
  }
}
